import { useMemo, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../api";
import SideRenderer from "../components/SideRenderer";

function noteMatches(note: { front: { text: string | null }; back: { text: string | null }; description: { text: string | null } }, needle: string): boolean {
  const haystacks = [note.front.text, note.back.text, note.description.text];
  return haystacks.some((h) => (h ?? "").toLowerCase().includes(needle));
}

export default function Deck() {
  const { deckId } = useParams();
  const id = Number(deckId);
  const navigate = useNavigate();
  const location = useLocation();
  const qc = useQueryClient();

  const decks = useQuery({ queryKey: ["decks"], queryFn: () => api.listDecks() });
  const notes = useQuery({
    queryKey: ["notes", id],
    queryFn: () => api.listNotes(id),
    enabled: Number.isInteger(id),
  });
  const deck = decks.data?.find((d) => d.id === id);
  const [query, setQuery] = useState("");
  const trimmedQuery = query.trim().toLowerCase();
  const filteredNotes = useMemo(() => {
    if (!notes.data) return undefined;
    if (trimmedQuery.length === 0) return notes.data;
    return notes.data.filter((n) => noteMatches(n, trimmedQuery));
  }, [notes.data, trimmedQuery]);

  const del = useMutation({
    mutationFn: (noteId: number) => api.deleteNote(noteId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["notes", id] });
      qc.invalidateQueries({ queryKey: ["decks"] });
    },
  });

  if (!Number.isInteger(id)) {
    return <div className="p-6 text-slate-500 dark:text-slate-400">Bad deck id.</div>;
  }

  const studyable = (deck?.dueCount ?? 0) + (deck?.newCount ?? 0);

  return (
    <div className="max-w-2xl mx-auto p-4 sm:p-6 space-y-5">
      <header className="flex items-center gap-3">
        <button onClick={() => navigate("/")} className="btn-secondary">←</button>
        <h1 className="text-2xl font-semibold truncate flex-1">{deck?.name ?? "Deck"}</h1>
        <Link to={`/decks/${id}/options`} className="btn-secondary" aria-label="Deck options" title="Options">
          ⚙
        </Link>
      </header>

      <div className="flex flex-wrap gap-2">
        <Link
          to={`/decks/${id}/study`}
          className={`btn-primary ${studyable === 0 ? "opacity-50 pointer-events-none" : ""}`}
        >
          Study ({studyable})
        </Link>
        <Link to={`/decks/${id}/new`} className="btn-secondary">+ Add card</Link>
      </div>

      <div className="relative">
        <input
          type="search"
          placeholder="Search this deck…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="input pr-10"
          aria-label="Search this deck"
        />
        {query && (
          <button
            type="button"
            onClick={() => setQuery("")}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 px-2"
            aria-label="Clear search"
          >
            ×
          </button>
        )}
      </div>

      {notes.isLoading && <div className="text-slate-500 dark:text-slate-400">Loading…</div>}

      {notes.data && notes.data.length === 0 && (
        <div className="text-slate-500 dark:text-slate-400">No cards yet — add one.</div>
      )}

      {trimmedQuery.length > 0 && filteredNotes && (
        <div className="text-xs text-slate-500 dark:text-slate-400">
          {filteredNotes.length} of {notes.data?.length ?? 0} match{filteredNotes.length === 1 ? "" : "es"}
        </div>
      )}

      <ul className="space-y-3">
        {filteredNotes?.map((note) => (
          <li key={note.id} className="card-surface p-4">
            <Link
              to={`/decks/${id}/notes/${note.id}`}
              state={{ from: location.pathname }}
              className="block space-y-2"
            >
              <div className="flex gap-3 items-start">
                <div className="flex-1 min-w-0">
                  <div className="text-xs uppercase tracking-wide text-slate-500 mb-1">Front</div>
                  <SideRenderer side={note.front} small />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-xs uppercase tracking-wide text-slate-500 mb-1 flex items-center gap-1">
                    <span>Back</span>
                    {!note.bidirectional && (
                      <span
                        className="text-[10px] font-normal lowercase text-slate-400 dark:text-slate-500"
                        title="One-way card (front → back only)"
                      >
                        one-way
                      </span>
                    )}
                  </div>
                  <SideRenderer side={note.back} small />
                </div>
              </div>
              {(note.description.text || note.description.image) && (
                <div className="pt-1 border-t border-slate-100 dark:border-slate-700">
                  <div className="text-xs uppercase tracking-wide text-slate-500 mb-1">Description</div>
                  {note.description.text && (
                    <div className="text-sm text-slate-600 dark:text-slate-400 whitespace-pre-wrap break-words">
                      {note.description.text}
                    </div>
                  )}
                  {note.description.image && (
                    <img
                      src={api.imageUrl(note.description.image)}
                      alt=""
                      className="mt-1 rounded-lg max-h-24"
                    />
                  )}
                </div>
              )}
            </Link>
            <div className="mt-2 text-right">
              <button
                onClick={() => {
                  if (confirm("Delete this card?")) del.mutate(note.id);
                }}
                className="text-slate-500 hover:text-rose-500 dark:hover:text-rose-400 text-sm"
              >
                Delete
              </button>
            </div>
          </li>
        ))}
      </ul>

    </div>
  );
}
